/**
 * Shape of every Laravel response. The backend wraps success as
 * `{ error: 0, result: <payload> }` and failures as `{ error: <code>, message }`.
 *
 * Careful: business failures are returned with **HTTP 200**, so the `error`
 * field — not the status code — is the source of truth. See BaseController.php.
 */
export interface ApiSuccessEnvelope<T> {
  error: 0
  result: T
}

export interface ApiErrorEnvelope {
  error: number
  message: string
}

export type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiErrorEnvelope

/**
 * Narrows an envelope to its success shape.
 *
 * A plain `envelope.error !== 0` check cannot discriminate the union on its
 * own, because the error variant is typed `number` — which includes 0.
 */
export function isSuccessEnvelope<T>(envelope: ApiEnvelope<T>): envelope is ApiSuccessEnvelope<T> {
  return envelope.error === 0
}

/** Mirrors App\Enum\ErrorTypeEnum on the backend. */
export const ApiErrorCode = {
  NONE: 0,
  GENERIC_ERROR: 1,
  INVALID_CREDENTIALS: 2,
  NOT_AUTHORIZED: 3,
  NOT_FOUND: 4,
  KEY_NOT_VALID: 5,
  ALREADY_IN_GROUP: 6,
  MAX_GROUP: 7,
  NO_PLAYER_FOUND: 8,
  ALLIANCE_NOT_ALLOWED: 9,
  PLAYER_ALREADY_REGISTERED: 10,
  PROVIDER_NOT_SUPPORTED: 11,
  SOCIAL_TOKEN_INVALID: 12,
  EMAIL_NOT_VERIFIED: 13,
  PLAYER_LOCKED_BY_GROUP: 14,
  /**
   * Dissociare il personaggio cancellerebbe le truppe/fucina dichiarate per lui:
   * il backend rifiuta finché non arriva `confirm: true`, e allega
   * `declared_villages`.
   */
  PLAYER_HAS_DECLARATIONS: 15,
  /** Ingresso da invito classico senza ruolo tattico: obbligatorio, controllato dopo le altre regole. Il dual lo eredita. */
  TACTICAL_ROLE_REQUIRED: 23,
} as const

export type ApiErrorCodeValue = (typeof ApiErrorCode)[keyof typeof ApiErrorCode]

/** Laravel 422 validation payload: `{ message, errors: { field: [msg, ...] } }`. */
export type ValidationErrors = Record<string, string[]>

/** Laravel's length-aware paginator, as returned by e.g. `GET /servers`. */
export interface Paginated<T> {
  current_page: number
  data: T[]
  first_page_url: string | null
  from: number | null
  last_page: number
  last_page_url: string | null
  next_page_url: string | null
  path: string
  per_page: number
  prev_page_url: string | null
  to: number | null
  total: number
}

/**
 * Normalised error thrown by the HTTP layer, whatever the transport-level
 * shape was (envelope error, 4xx/5xx, network failure).
 */
export class ApiError extends Error {
  /** Backend error code, or `null` for transport/network errors. */
  readonly code: number | null
  /** HTTP status, or `null` when the request never got a response. */
  readonly status: number | null
  /** Per-field messages from a 422 response. */
  readonly fieldErrors: ValidationErrors
  /**
   * i18n key for errors we generate client-side (network down, session
   * expired, ...). Backend messages arrive as plain English text instead, so
   * the notifier prefers this key whenever it is set.
   */
  readonly translationKey: string | null
  /**
   * Extra top-level keys the backend attached to the error envelope
   * (`errorResponseData`), for the cases where the client has to *decide* on a
   * number rather than just show a sentence — e.g. how many villages a detach
   * would wipe.
   */
  readonly data: Record<string, unknown>

  constructor(
    message: string,
    options: {
      code?: number | null
      status?: number | null
      fieldErrors?: ValidationErrors
      translationKey?: string
      data?: Record<string, unknown>
    } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = options.code ?? null
    this.status = options.status ?? null
    this.fieldErrors = options.fieldErrors ?? {}
    this.translationKey = options.translationKey ?? null
    this.data = options.data ?? {}
  }

  /** True when the account exists but the email was never verified. */
  get isEmailNotVerified(): boolean {
    return this.code === ApiErrorCode.EMAIL_NOT_VERIFIED
  }

  get isInvalidCredentials(): boolean {
    return this.code === ApiErrorCode.INVALID_CREDENTIALS
  }

  /** First message for a field, if the backend reported one. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0]
  }
}

/** Extra per-request options understood by our axios interceptors. */
export interface RequestMeta {
  /** Skip the automatic red toast (the caller renders the error itself). */
  silent?: boolean
  /** Never attempt a token refresh/retry for this request. */
  skipAuthRefresh?: boolean
}
